/* JourneyBuilder.js */

import React, { useCallback, useEffect, useMemo, useState, Fragment, useRef } from 'react';
import {ReactFlow, addEdge, Background, Controls, MiniMap, useEdgesState, useNodesState, Handle, Position, MarkerType, ReactFlowProvider } from '@xyflow/react';
import { Container, Row, Col, Card, Modal, Button, Form } from "react-bootstrap";
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import { useDispatch, useSelector } from 'react-redux';
import '@xyflow/react/dist/style.css';
import "./Custom.css";

import { fetchDetalil, updateJourney, fetchTemplates, whatsAppTemplateList, updateWhatsappTemplate, notificationDetailList } from "../../../redux/actions/journeyAction";

import WhatsappDrawer from './journeyComponent/WhatsappDrawer';
import PushDrawer from './journeyComponent/PushDrawer';
// Import custom node components
import { SuccessEdge, FailureEdge } from './journeyComponent/edges/CustomEdges';
import ForSpecificUsersNode from './journeyComponent/nodes/ForSpecificUsersNode';
import SegmentNode from './journeyComponent/nodes/SegmentNode';
import { ChevronDown, ChevronUp, Download, Upload, Whatsapp,   ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import IsInSegmentNode from './journeyComponent/nodes/IsInSegmentNode';
import HasDoneEventNode from './journeyComponent/nodes/HasDoneEventNode';
import CheckUserAttributeNode from './journeyComponent/nodes/CheckUserAttributeNode';
import IsUserReachableNode from './journeyComponent/nodes/IsUserReachableNode';
import OccurrenceNode from './journeyComponent/nodes/OccurrenceNode';
import UserAttributeNode from './journeyComponent/nodes/UserAttributeNode';
import SendEmailNode from './journeyComponent/nodes/SendEmailNode';
import SendSMSNode from './journeyComponent/nodes/SendSMSNode';
import ShowInAppNode from './journeyComponent/nodes/ShowInAppNode';
import CallAPINode from './journeyComponent/nodes/CallAPINode';
import EndJourneyNode from './journeyComponent/nodes/EndJourneyNode';
import SendPushNode from './journeyComponent/nodes/SendPushNode';
import ShowOnSiteNotificationNode from './journeyComponent/nodes/ShowOnSiteNotificationNode';
import SendWebPushNode from './journeyComponent/nodes/SendWebPushNode';
import SendWhatsAppNode from './journeyComponent/nodes/SendWhatsAppNode';
import SetUserAttributeNode from './journeyComponent/nodes/SetUserAttributeNode';
import WaitForSomeTimeNode from './journeyComponent/nodes/WaitForSomeTimeNode';
import WaitForTimeSlotsNode from './journeyComponent/nodes/WaitForTimeSlotsNode';
import WaitForEventNode from './journeyComponent/nodes/WaitForEventNode';
import WaitForDateNode from './journeyComponent/nodes/WaitForDateNode';
import EmailDrawer from './journeyComponent/EmailDrawer';
import Toast from "../../shared/Toast"
import DateDrawer from './journeyComponent/drawer/DateDrawer';
import WaitTimeDrawer from './journeyComponent/drawer/WaitTimeDrawer';

// Add this new import for the spinner icon
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { BsFloppy, BsXCircle} from 'react-icons/bs';
import HasDoneEventDrawer from './journeyComponent/HasDoneEventDrawer';
import WaitTimeSlotsDrawer from './journeyComponent/drawer/WaitTimeSlotsDrawer';
import StarNode from './journeyComponent/nodes/StarNode';
import UserAttributeDrawer from './journeyComponent/drawer/UserAttributeDrawer';

import { useNavigate, useParams } from 'react-router-dom';

import {CalendarDaysIcon, ChartPieIcon, CheckCircleIcon, ClockIcon, Cog6ToothIcon, EnvelopeIcon, BellIcon, HandRaisedIcon, PhoneXMarkIcon, PowerIcon, RocketLaunchIcon,
} from "@heroicons/react/24/outline";

// Add mock segments data
const segments = [
  "All Users",
  "Active Users",
  "Inactive Users",
  "New Users",
  "Premium Users",
  "Free Users",
  "High Value Customers",
  "At Risk Users",
  "Churned Users",
  "Beta Users"
];

// Add this near the top of your file, after the imports
const edgeOptions = {
  style: { strokeWidth: 2 },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#b1b1b7',
  },
};


/* Collapsible Section */
const CollapsibleSection = ({ title, items, color }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="collapsible-card mb-2">
      <Card.Header
        className="collapsible-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </Card.Header>

      <div className={`collapsible-body ${isOpen ? "open" : ""}`}>
        <Row className="p-2">
          {items.map((item) => (
            <Col
              key={item.label}
              xs={4}
              className="text-center draggable-item mb-2"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData("application/reactflow", item.type);
                event.dataTransfer.setData("application/source", item.source);
              }}
            >
              {title !== "Stakeholders" ? (
                <>
                  <div
                    className="icon-circle"
                    style={{ backgroundColor: color }}
                  >
                    {item.icon &&
                      React.createElement(
                        item.icon,
                        item.icon.name?.includes("Icon")
                          ? { className: "w-5 h-5 text-white" }
                          : { size: 20, color: "#fff" }
                      )}
                  </div>
                  <div className="icon-label">{item.label}</div>
                </>
              ) : (
                <div
                  className="stakeholder-circle"
                  style={{
                    border: item.border || "#6c757d",
                    backgroundColor: item.color || "#f8f9fa",
                  }}
                >
                  <span className="stakeholder-label">{item.label}</span>
                </div>
              )}
            </Col>
          ))}
        </Row>
      </div>
    </Card>
  );
};

/* Custom Node */
const CustomNode = ({ data }) => (
  <div className="custom-node" style={{ backgroundColor: data.bgColor }}>
    {data.label}
  </div>
);

const nodeTypes = { custom: CustomNode };

const JourneyBuilder = (props) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const reactFlowInstanceRef = useRef(null);
  const journeydetailas = useSelector((state) => state.journey.journey_detail || {});
  const templates = useSelector((state) => state.templates.templates || []);
  const templatesLoading = useSelector((state) => state.templates.loading);
  const whatsappTemplates = useSelector((state) => state.whatsappTemplates.whatsappTemplates || []);
  const whatsappTemplatesLoading = useSelector((state) => state.whatsappTemplates.loading);
  const journeyLoading = useSelector((state) => state.journey.loading);
  const journeyUpdateSuccess = useSelector((state) => state.journey.journeyUpdateSuccess);
  const journeyUpdateError = useSelector((state) => state.journey.journeyUpdateError);
  // Debug Redux state changes
  useEffect(() => {
  }, [journeyUpdateSuccess, journeyUpdateError]);

  const authToken = props.authToken;
  const navigate = useNavigate();


  // Authentication check
  useEffect(() => {
    if (!localStorage.getItem("userData")) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (authToken && id) {
      // Clear local state immediately on ID change to avoid flashing old graph
      setNodes(initialNodes);
      setEdges([]);
      dispatch(fetchDetalil(authToken, id));
      dispatch(fetchTemplates(authToken, {
        action: "select_template",
        filter: "all",
        search: "",
        page: 1,
        limit: 50
      }));
      dispatch(whatsAppTemplateList(authToken, {
       
      }));

      dispatch(notificationDetailList(authToken, id));

    }
  }, [dispatch, authToken, id]);

  const is_direct_email = journeydetailas.is_autoemail===1 ? false : true;

  useEffect(() => {
    if (journeydetailas?.details) {
      // Ignore responses for a different journey id
      const detailsId = journeydetailas.details.id;
      if (detailsId !== undefined && String(detailsId) !== String(id)) return;

      const apiNodes = Array.isArray(journeydetailas.details.nodes) ? journeydetailas.details.nodes : [];
      const apiEdges = Array.isArray(journeydetailas.details.edges) ? journeydetailas.details.edges : [];
      setNodes(apiNodes.length ? apiNodes : initialNodes);
      setEdges(apiEdges);
    }
  }, [journeydetailas, id]);


  // Normalize API data → ensure ids are strings, add required handlers and labels
  useEffect(() => {
    if (!journeydetailas) return;

    // Guard against stale responses from another journey id
    const detailsId = journeydetailas?.details?.id ?? journeydetailas?.id;
    if (detailsId !== undefined && String(detailsId) !== String(id)) return;

    const sourceNodes = Array.isArray(journeydetailas?.details?.nodes)
      ? journeydetailas.details.nodes
      : (Array.isArray(journeydetailas?.nodes) ? journeydetailas.nodes : []);
    const sourceEdges = Array.isArray(journeydetailas?.details?.edges)
      ? journeydetailas.details.edges
      : (Array.isArray(journeydetailas?.edges) ? journeydetailas.edges : []);

    // If API returned no nodes/edges, show default starter node and clear edges
    if (sourceNodes.length === 0 && sourceEdges.length === 0) {
      setNodes(initialNodes);
      setEdges([]);
      return;
    }

    // Helper to compute an edge label from a source node and handle index
    const computeEdgeLabel = (sourceNode, handleIndex) => {
      if (!sourceNode) return '';
      if (sourceNode.type === 'sendPush') {
        if (sourceNode.data?.source === 'seller' || sourceNode.data?.source === 'manager') return 'Notify';
        //const edgeLabels = ['Click', 'Ignore', 'Interact', 'Block'];
        const edgeLabels = [];
        return edgeLabels[handleIndex] || '';
      }
      if (sourceNode.type === 'sendWhatsApp') {
        if (sourceNode.data?.source === 'seller' || sourceNode.data?.source === 'manager') return 'Notify';
        //const edgeLabels = ['Send', 'Read', 'Click', 'Ignore'];
        const edgeLabels = [];
        return edgeLabels[handleIndex] || '';
      }
      if (sourceNode.type === 'sendEmail') {
        if (sourceNode.data?.source === 'seller' || sourceNode.data?.source === 'manager') return 'Notify';
        const edgeLabels = ['Send', 'Open', 'Click', 'Not Clicked', 'Bounce', 'Unopened'];
        return edgeLabels[handleIndex] || '';
      }
      if (
        sourceNode.type === 'isInSegment' ||
        sourceNode.type === 'hasDoneEvent' ||
        sourceNode.type === 'checkuserAttribute' ||
        sourceNode.type === 'isUserReachable'
      ) {
        const edgeLabels = ['Yes', 'No'];
        return edgeLabels[handleIndex] || '';
      }
      return '';
    };

    if (sourceNodes.length) {
      const loadedNodes = sourceNodes.map((n) => {
        const nodeId = String(n.id);
        const nodeType = n.type;
        return {
          id: nodeId,
          type: nodeType,
          position: n.position || { x: 0, y: 0 },
          data: {
            ...(n.data || {}),
            onEdgeCountChange: (newCount) => handleEdgeCountChange(nodeId, newCount),
            onDelete: () => handleNodeDelete(nodeId),
            onEdgeDelete: (handleId) => handleEdgeDelete(nodeId, handleId),
            outputEdges: defaultNodeEdges[nodeType] || 1,
          },
        };
      });

      // Seed counts for dynamic output renders
      const initialCounts = {};
      loadedNodes.forEach((node) => {
        initialCounts[node.id] = defaultNodeEdges[node.type] || 1;
      });
      setNodeEdgeCounts(initialCounts);
      setNodes(loadedNodes);
    }

    if (sourceEdges.length) {
      // We need access to nodes to compute labels; use the just-built normalized nodes
      const nodeMap = new Map();
      nodes.forEach((n) => nodeMap.set(n.id, n));

      const loadedEdges = sourceEdges.map((e, idx) => {
        const id = String(e.id ?? `e-${idx}`);
        const source = String(e.source);
        const target = String(e.target);
        // Normalize sourceHandle; accept either a number index or a string like 'o-2'
        let sourceHandle = e.sourceHandle;
        if (typeof sourceHandle === 'number') sourceHandle = `o-${sourceHandle}`;
        if (!sourceHandle && typeof e.handleIndex === 'number') sourceHandle = `o-${e.handleIndex}`;
        // Derive handle index for label selection
        const handleIndex = parseInt(String(sourceHandle || '').split('-')[1] || '0');
        const sourceNode = nodeMap.get(source) || nodes.find((n) => n.id === source);
        const label = e.label || computeEdgeLabel(sourceNode, handleIndex);
        return {
          id,
          source,
          target,
          sourceHandle: sourceHandle || undefined,
          targetHandle: e.targetHandle || undefined,
          type: e.type || 'default',
          ...edgeOptions,
          labelX: e.labelX ?? 30,
          labelY: e.labelY ?? -10,
          label,
          labelStyle: e.labelStyle || { fill: '#888', fontSize: 12 },
          labelBgStyle: e.labelBgStyle || { fill: 'white' },
          labelBgPadding: e.labelBgPadding || [28, 4],
          labelBgBorderRadius: e.labelBgBorderRadius || 4,
        };
      });
      setEdges(loadedEdges);
    }
  }, [journeydetailas]);

  // Debug templates
  useEffect(() => {
   
  }, [templates, templatesLoading, authToken]);
  
  /* Sidebar Sections */
  const nodeTypesData = [
    {
      title: "Actions",
      items: [
        { label: "Send Email", icon: EnvelopeIcon, type: "sendEmail", source: "actions" },
        { label: 'Send WhatsApp', icon: Whatsapp, type: 'sendWhatsApp', source: 'actions' },
        { label: 'Send Push', icon: BellIcon, type: 'sendPush', source: 'actions' },
      
      ],
      color: "#19945980",
    },
    {
      title: "Conditions",
      items: [
        { label: "Is in Segment", icon: ChartPieIcon, type: "isInSegment" },
        { label: "Has Done Event", icon: CheckCircleIcon, type: "hasDoneEvent" },
        { label: "Check User Attribute", icon: Cog6ToothIcon, type: "checkuserAttribute" },
        { label: "Is User Reachable", icon: PhoneXMarkIcon, type: "isUserReachable" },
      ],
      color: "#f6971a80",
    },
    {
      title: "Flow Controls",
      items: [
        { label: "Wait for Some Time", icon: HandRaisedIcon, type: "waitForSomeTime" },
        { label: "Wait for Time Slots", icon: ClockIcon, type: "waitForTimeSlots" },
        { label: "Wait for Date", icon: CalendarDaysIcon, type: "waitForDate" },
        { label: "End Journey", icon: PowerIcon, type: "endJourney" },
      ],
      color: "#d6201c80",
    },
    {
      title: "Stakeholders",
      items: [
        { label: "All Users", icon: Whatsapp, type: "mfd", source: "stakeholders", color: "#f6971a80", border: "2px solid #f6971a80" },
        { label: "Admin Users", icon: EnvelopeIcon, type: "manager", source: "stakeholders", color: "#45bcd280", border: "2px solid #bfdbfe" },

      ],
      color: "#199459",
    },
  ];

  // Add this near the nodeTypesData definition
  const defaultNodeEdges = {
    segment: 1,
    occurrence: 1,
    isInSegment: 2,
    hasDoneEvent: 2,
    isUserReachable: 2,
    checkuserAttribute: 2,
    specificUsers: 1,
    userAttribute: 1,
    sendEmail: 6,
    sendSMS: 4,
    showInApp: 2,
    callAPI: 4,
    endJourney: 1,
    sendPush: 2,
    showOnSiteNotification: 4,
    sendWebPush: 4,
    sendWhatsApp: 1,
    setUserAttribute: 4,
    waitForSomeTime: 1,
    waitForTimeSlots: 1,
    waitForEvent: 1,
    waitForDate: 1
  };

    // Add state to track node edge counts
  const [nodeEdgeCounts, setNodeEdgeCounts] = useState({});
  const [selectedSegment, setSelectedSegment] = useState('');
  // Add these new states after your existing drawer states
const [isUserAttributeDrawerOpen, setIsUserAttributeDrawerOpen] = useState(false);
const [selectedUserAttributeNode, setSelectedUserAttributeNode] = useState(null);
  // Modify the initialNodes array
  const initialNodes = [
    {
      id: 'initial-segment',
      type: 'segment',
      position: { x: 100, y: 200 },
      data: { 
        label: 'Segment',
        segmentName: selectedSegment || 'All Users',
        onEdgeCountChange: (newCount) => handleEdgeCountChange('initial-segment', newCount),
        outputEdges: defaultNodeEdges.segment,
      },
    }
  ];
  const initialEdges = []; // Start with no edges

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
   
  // Ensure initial segment node is set when component mounts
  useEffect(() => {
    if (nodes.length === 0) {
      setNodes(initialNodes);
    }
  }, []);
   
  // Add new state for journey dialog
  const [showJourneyDialog, setShowJourneyDialog] = useState(false);

  // Add these states in your JourneyBuilder component
  const [isEmailDrawerOpen, setIsEmailDrawerOpen] = useState(false);
  const [selectedEmailNode, setSelectedEmailNode] = useState(null);
  
  const [journeyName, setJourneyName] = useState('');
  
  const [loaderSaveJourney, setLoaderSaveJourney] = useState(false);
  const [loaderCloseJourney, setLoaderCloseJourney] = useState(false);
  
  // Reset loader when journey loading state changes
  useEffect(() => {
    if (!journeyLoading && loaderSaveJourney) {
      setLoaderSaveJourney(false);
    }
  }, [journeyLoading, loaderSaveJourney]);
  
  const [isWhatsappDrawerOpen, setIsWhatsappDrawerOpen] = useState(false);
  const [isPushDrawerOpen, setIsPushDrawerOpen] = useState(false);
  const [selectedWhatsappNode, setSelectedWhatsappNode] = useState(null);
  const [selectedPushNode, setSelectedPushNode] = useState(null);
  
  // Add new state for date drawer
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState(false);
  const [selectedDateNode, setSelectedDateNode] = useState(null);
  
  // Add new state for wait time drawer
  const [isWaitTimeDrawerOpen, setIsWaitTimeDrawerOpen] = useState(false);
  const [selectedWaitTimeNode, setSelectedWaitTimeNode] = useState(null);
  
  // Add these new states in the JourneyBuilder component
  const [isWaitTimeSlotsDrawerOpen, setIsWaitTimeSlotsDrawerOpen] = useState(false);
  const [selectedWaitTimeSlotsNode, setSelectedWaitTimeSlotsNode] = useState(null);
  
  const [isHasDoneEventDrawerOpen, setIsHasDoneEventDrawerOpen] = useState(false);
  const [selectedHasDoneEventNode, setSelectedHasDoneEventNode] = useState(null);
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Add this useEffect to update the initial segment node when selectedSegment changes
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === 'initial-segment'
          ? {
              ...node,
              data: {
                ...node.data,
                segmentName: selectedSegment || 'All Users',
              },
            }
          : node
      )
    );
  }, [selectedSegment]);

  const onConnect = useCallback(
    (params) => {
      // Get the source node and output handle ID
      const sourceNode = nodes.find(node => node.id === params.source);
      const outputIndex = parseInt(params.sourceHandle?.split('-')[1] || 0);
      
      // Get the label based on node type
      let label = '';
      if (sourceNode?.type === 'sendPush') {
        if (sourceNode.data.source === 'seller' || sourceNode.data.source === 'manager') {
          label = 'Notify';
        } else {
          const edgeLabels = ['Send', 'Click', 'Interact', 'Block'];
          label = edgeLabels[outputIndex];
        }
      } else if (sourceNode?.type === 'sendWhatsApp') {
        // Check the source type for WhatsApp nodes
        if (sourceNode.data.source === 'seller' || sourceNode.data.source === 'manager') {
          label = 'Notify';
        } else {
          const edgeLabels = ['Send', 'Read', 'Click', 'Ignore'];
          label = edgeLabels[outputIndex];
        }
      } else if (sourceNode?.type === 'sendEmail') {
        if (sourceNode.data.source === 'seller' || sourceNode.data.source === 'manager') {
          label = 'Notify';
        } else {
          const edgeLabels = ['Send', 'Open', 'Click', 'Not Clicked', 'Bounce', 'Unopened'];
          label = edgeLabels[outputIndex];
        }
      } else if (sourceNode?.type === 'isInSegment' || sourceNode?.type === 'hasDoneEvent' || sourceNode?.type === 'checkuserAttribute' || sourceNode?.type === 'isUserReachable') {
        const edgeLabels = ['Yes', 'No'];
        label = edgeLabels[outputIndex];
      }

      const edge = {
        ...params,
        ...edgeOptions,  // Add default edge options
        labelX: 30, // Adjust this value to fine-tune the label position
        labelY: -10,
        label: label,
        labelStyle: { fill: '#888', fontSize: 12 },
        labelBgStyle: { fill: 'white' },
        labelBgPadding: [28, 4],
        labelBgBorderRadius: 4
      };

      setEdges((eds) => addEdge(edge, eds));
    },
    [nodes, setEdges]
  );

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Add state to track node edge counts
  //const [nodeEdgeCounts, setNodeEdgeCounts] = useState({});
  // Add handler for updating edge counts
  const handleEdgeCountChange = useCallback((nodeId, newCount) => {
    setNodeEdgeCounts(prev => ({
      ...prev,
      [nodeId]: newCount
    }));
  }, []);
  // Add these handlers for delete operations
  const handleNodeDelete = useCallback((nodeId) => {
    setNodes((nodes) => nodes.filter((node) => node.id !== nodeId));
    // Also remove any connected edges
    setEdges((edges) => edges.filter((edge) => 
      edge.source !== nodeId && edge.target !== nodeId
    ));
  }, [setNodes, setEdges]);

  const handleEdgeDelete = useCallback((nodeId, handleId) => {
    setEdges((edges) => edges.filter((edge) => 
      !(edge.source === nodeId && edge.sourceHandle === handleId)
    ));
  }, [setEdges]);

  // Modify the onDrop callback to include delete handlers
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const source = event.dataTransfer.getData('application/source');
      // Use React Flow instance to convert screen coordinates to flow coordinates
      const instance = reactFlowInstanceRef.current;
      const flowPosition = instance && instance.screenToFlowPosition
        ? instance.screenToFlowPosition({ x: event.clientX, y: event.clientY })
        : { x: event.clientX, y: event.clientY };
      const position = { x: flowPosition.x, y: flowPosition.y };

      const newNodeId = `${Date.now()}`;
      
      if (defaultNodeEdges[type]) {
        setNodeEdgeCounts(prev => ({
          ...prev,
          [newNodeId]: defaultNodeEdges[type]
        }));
      }
      const newNode = {
        id: newNodeId,
        type,
        position,
        data: { 
          label: type === 'sendEmail' ? 'Send Email' : type === 'sendWhatsApp' ? 'Send Whatsapp' : type === 'mfd' ? 'All Users' :
          type === 'manager' ? 'Admin Users' :
          type === 'saleshead' ? 'Sales Head' :
          type === 'branchhead' ? 'Branch Head':type, // Set default label for email nodes
          source: source,
          onEdgeCountChange: (newCount) => handleEdgeCountChange(newNodeId, newCount),
          outputEdges: defaultNodeEdges[type],
          onDelete: handleNodeDelete,
          onEdgeDelete: handleEdgeDelete
        },
      };

      setNodes((nds) => nds.concat(newNode));

      // Open email drawer if the node type is sendEmail
      if (type === 'sendEmail') {
        setSelectedEmailNode(newNode);
        // setIsEmailDrawerOpen(true);
      }
      if (type === 'sendWhatsApp') {
        setSelectedWhatsappNode(newNode);
        // setIsWhatsappDrawerOpen(true);
      }
      if (type === 'sendPush') {
        setSelectedPushNode(newNode);
       // setIsPushDrawerOpen(true);
      }
    },
    [setNodes, handleEdgeCountChange, handleNodeDelete, handleEdgeDelete]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Memoize nodeTypes
  const nodeTypes = useMemo(() => ({
    input: ({ data }) => (
      <div>
        <div className="p-3 py-5 gradient-info rounded cursor-move d-flex justify-content-center align-items-center gap-2">
          <RocketLaunchIcon className='w-10 h-10 p-1.5 bg-white text-info-custom rounded-full'/>
          <span className="fw-semibold fs-5 text-info-custom">Start Journey</span>
        </div>
        <Handle
          type="source"
          position={Position.Right}
          className="small-handle"
        />
      </div>
    ),
    segment: (props) => (
      <SegmentNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
        onClick={props.id === 'initial-segment' ? () => setShowJourneyDialog(false) : undefined}
        isInitial={props.id === 'initial-segment'}
      />
    ),
    occurrence: (props) => (
      <OccurrenceNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    isInSegment: (props) => (
      <IsInSegmentNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    hasDoneEvent: (props) => (
      <HasDoneEventNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    isUserReachable: (props) => (
      <IsUserReachableNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    checkuserAttribute: (props) => (
      <CheckUserAttributeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    specificUsers: (props) => (
      <ForSpecificUsersNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    userAttribute: (props) => (
      <UserAttributeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendEmail: (props) => (
      <SendEmailNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendSMS: (props) => (
      <SendSMSNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    showInApp: (props) => (
      <ShowInAppNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    callAPI: (props) => (
      <CallAPINode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    endJourney: (props) => (
      <EndJourneyNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendPush: (props) => (
      <SendPushNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    showOnSiteNotification: (props) => (
      <ShowOnSiteNotificationNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendWebPush: (props) => (
      <SendWebPushNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendWhatsApp: (props) => (
      <SendWhatsAppNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    setUserAttribute: (props) => (
      <SetUserAttributeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForSomeTime: (props) => (
      <WaitForSomeTimeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForTimeSlots: (props) => (
      <WaitForTimeSlotsNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForEvent: (props) => (
      <WaitForEventNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForDate: (props) => (
      <WaitForDateNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.waitForDate}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    mfd: StarNode,
    manager: StarNode,
    saleshead: StarNode,
    branchhead: StarNode,
  }), [nodeEdgeCounts, handleEdgeCountChange]); // Empty dependency array since these components don't change

  // Memoize edgeTypes
  const edgeTypes = useMemo(() => ({
    success: SuccessEdge,
    failure: FailureEdge,
  }), []); // Empty dependency array since these components don't change

  // Add this useEffect to handle the ResizeObserver error
  useEffect(() => {
    window.addEventListener('error', e => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message === 'ResizeObserver loop limit exceeded') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.style.display = 'none';
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.style.display = 'none';
        }
      }
    });
  }, []);


  const handleHasDoneEventSave = (eventConfig) => {
    if (selectedHasDoneEventNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedHasDoneEventNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  eventConfig,
                  label: `${eventConfig.eventName}`, // Update node label
                },
              }
            : node
        )
      );
    }
    setIsHasDoneEventDrawerOpen(false);
    setSelectedHasDoneEventNode(null);
  };
  // ... rest of the code ...
  
    // Add preview panel component
    const PreviewPanel = ({ nodes, edges, selectedNode }) => {
      // Helper function to get edge type label
      const getEdgeLabel = (edge) => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        if (sourceNode?.type === 'decision') {
          return edge.type === 'success' ? 'Yes' : 'No';
        }
        return '';
      };
  
      // Helper function to format node details
      const getNodeDetails = (node) => {
        switch (node.type) {
          case 'wait':
            return `(${node.data.waitDays || 1} days)`;
          case 'decision':
            return `(${node.data.condition || 'Default Condition'})`;
          default:
            return '';
        }
      };
  
      return (
        <div className="space-y-4 custom-scrollbar">
          <h3 className="fs-5 fw-semibold mb-4">Journey Preview</h3>
          
          {/* Journey Summary */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">Total Steps: {nodes.length}</p>
            <p className="text-sm text-gray-600">Connections: {edges.length}</p>
          </div>
  
          {/* Journey Flow Preview */}
          <div className="space-y-2">
            {nodes.map((node) => (
              <div
                key={node.id}
                className={`p-3 rounded border ${
                  selectedNode?.id === node.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-secondary-subtle'
                }`}
              >
                <div className="d-flex align-items-center gap-2">
                  {/* Node type icon */}
                  <span>
                    {node.type === 'email' && '📧'}
                    {node.type === 'sms' && '💬'}
                    {node.type === 'webpush' && '🔔'}
                    {node.type === 'wait' && '⏳'}
                    {node.type === 'landingPage' && '🌐'}
                    {node.type === 'decision' && '🔀'}
                    {node.type === 'merge' && '🔄'}
                    {node.type === 'input' && '🚀'}
                    {node.type === 'ai' && '🤖'}
                  </span>
                  <span className="text-sm font-medium">
                    {node.data.label} {getNodeDetails(node)}
                  </span>
                </div>
                
                {/* Connected nodes */}
                {edges
                  .filter((edge) => edge.source === node.id)
                  .map((edge) => {
                    const targetNode = nodes.find(n => n.id === edge.target);
                    return (
                      <div key={edge.id} className="ml-6 mt-2 text-sm text-muted">
                        ↳ {getEdgeLabel(edge)} Connects to: {targetNode?.data.label} {getNodeDetails(targetNode)}
                      </div>
                    );
                  })}
              </div>
            ))}
          </div>
  
          {/* Selected Node Details */}
          {selectedNode && (
            <div className="mt-6 p-4 bg-light rounded">
              <h4 className="font-medium mb-2">Selected Node Details</h4>
              <p className="text-sm text-gray-600">Type: {selectedNode.type}</p>
              <p className="text-sm text-gray-600">ID: {selectedNode.id}</p>
              <p className="text-sm text-gray-600">Label: {selectedNode.data.label}</p>
              {selectedNode.type === 'wait' && (
                <p className="text-sm text-gray-600">Wait Duration: {selectedNode.data.waitDays} days</p>
              )}
              {selectedNode.type === 'decision' && (
                <>
                  <p className="text-sm text-gray-600">Condition: {selectedNode.data.condition}</p>
                  <p className="text-sm text-gray-600">Yes Path: {selectedNode.data.yesLabel}</p>
                  <p className="text-sm text-gray-600">No Path: {selectedNode.data.noLabel}</p>
                </>
              )}
            </div>
          )}
        </div>
      );
    };


  // Add new state for preview panel
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Add a hidden file input element and reference it
  const fileInputRef = useRef(null);

  // Modify the button to trigger file input
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  // Function to handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const journeyData = JSON.parse(e.target.result);
          
          // Set journey name and segment from imported data
          setJourneyName(journeyData.name || '');
          setSelectedSegment(journeyData.segment || '');
          
          // Update nodes with necessary handlers and correct edge counts
          const nodesWithHandlers = journeyData.nodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              onEdgeCountChange: (newCount) => handleEdgeCountChange(node.id, newCount),
              onDelete: handleNodeDelete,
              onEdgeDelete: handleEdgeDelete,
              outputEdges: defaultNodeEdges[node.type] || 1, // Use the default edge count for the node type
            }
          }));

          // Initialize node edge counts
          const initialEdgeCounts = {};
          nodesWithHandlers.forEach(node => {
            initialEdgeCounts[node.id] = defaultNodeEdges[node.type] || 1;
          });
          setNodeEdgeCounts(initialEdgeCounts);

          // Set nodes and edges
          setNodes(nodesWithHandlers);
          setEdges(journeyData.edges || []);
          
          // Close journey dialog if open
          setShowJourneyDialog(false);
        } catch (error) {
          // You might want to show an error message to the user here
        }
      };
      reader.readAsText(file);
    }
  };

  // Add new state for segment statistics
  const [segmentStats, setSegmentStats] = useState({
    totalUsers: 525711,
    knownUsers: 124994,
    unknownUsers: 400718,
    reachableUsers: 348513
  });
  

  // Function to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Add these handlers
  const handleEmailDrawerClose = () => {
    setIsEmailDrawerOpen(false);
    setSelectedEmailNode(null);
    
  };
  const handleWhatsappDrawerClose = () => {
    setIsWhatsappDrawerOpen(false);
    setSelectedWhatsappNode(null);
  };

  const handlePushDrawerClose = () => {
    setIsPushDrawerOpen(false);
    setSelectedPushNode(null);
  };

  const handleWhatsappDrawerSave = async (whatsappData) => {
    try {
      const jbId = id; // journey id from route params
      const payload = {
        wp_id: whatsappData.wp_id,
        title: whatsappData.title,
        description: whatsappData.description || "",
        jb_id: jbId,
        node_id: selectedWhatsappNode.id,
        type: "whatsapp",
        image: whatsappData.image || null,
        link: "",
        image_removed: whatsappData.image_removed ?? 0,
        file_type: whatsappData.file_type || null,
      };
      if (!payload.image) {
        delete payload.image;
      }
      const response = await dispatch(updateWhatsappTemplate(authToken, payload));
      if (response?.statusCode == "200") {
        const notificationData = response?.details?.notification_id;
        whatsappData.notification_id = notificationData;
        // Refresh latest details so subsequent edit shows the new image/url
        await dispatch(notificationDetailList(authToken, id));
      } 
    } catch (e) {}

    if (selectedWhatsappNode) {
      // Clean whatsapp data - don't store image data in node state
      const cleanWhatsappData = { ...whatsappData };
      // Remove the full image object to avoid storing large data
      delete cleanWhatsappData.image;
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedWhatsappNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  whatsappConfig: cleanWhatsappData,
                  label: cleanWhatsappData.title || 'Send Whatsapp',
                },
              }
            : node
        )
      );
    }

    setIsWhatsappDrawerOpen(false);
    setSelectedWhatsappNode(null);
  };

  const handlePushDrawerSave = async (pushData) => {
    try {
      const jbId = id; // journey id from route params
      const payload = {
        wp_id: '', // Using wp_id field for push template ID
        title: pushData.title,
        link: pushData.link || '',
        description: pushData.message || pushData.description || "", // Use message as description for push
        image: pushData.image || null,
        jb_id: jbId,
        node_id: selectedPushNode?.id || "", // Add node ID
        type: "push", // Different type to distinguish from WhatsApp
        image_removed: pushData.image_removed ?? 0,
        file_type: null, // Push notifications don't use file_type
      };
      // If image is empty, omit it from the payload entirely
      if (!payload.image) {
        delete payload.image;
      }
      const response = await dispatch(updateWhatsappTemplate(authToken, payload));
      if (response?.statusCode == "200") {
        const notificationData = response?.details?.notification_id;
        pushData.notification_id = notificationData;
        // Refresh latest details so subsequent edit shows the new image/url
        await dispatch(notificationDetailList(authToken, id));
      } 
    } catch (e) {
      Toast("Failed to update push template", "E");
    }

    if (selectedPushNode) {
      // Avoid storing large base64 image in node state
      const cleanPushData = { ...pushData };
      delete cleanPushData.image;
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedPushNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  pushConfig: cleanPushData,
                  label: cleanPushData.title || 'Send Push',
                },
              }
            : node
        )
      );
    }
    handlePushDrawerClose();
  };

  const handleEmailDrawerSave = (emailData) => {
    if (selectedEmailNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedEmailNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  emailConfig: emailData,
                  label: emailData.campaignName || 'Send Email', // Add the campaign name as the node label
                  templateId: emailData.template,
                  templateTitle: emailData.templateTitle,
                  templateMessage: emailData.templateMessage,
                },
              }
            : node
        )
      );
    }
    handleEmailDrawerClose();
  };

  const createJourney = async () => {
    try {
      const response = await createJourney("journeyBuilder/create_journey",{id:"",title:journeyName,segment:selectedSegment});
      if(response?.statusCode == "200"){
        Toast("Journey Created Successfully.", "S");
        setShowJourneyDialog(false);
      }
    } catch (error) {
      Toast("Failed to fetch journeys", "E");
      setShowJourneyDialog(false);
    }
  };

  const saveJourneyData = async (id, journeyData) => {
    try {
      await dispatch(updateJourney(authToken, id, journeyData));
    } catch (error) {
      Toast("Failed to save journey", "E");
      setLoaderSaveJourney(false);
    }
  };

  // Function to sort nodes in logical flow order
  const sortNodesInFlowOrder = (nodes, edges) => {
    // Create a map of node connections
    const nodeConnections = new Map();
    const visited = new Set();
    const sortedNodes = [];

    // Build connection map
    edges.forEach(edge => {
      if (!nodeConnections.has(edge.source)) {
        nodeConnections.set(edge.source, []);
      }
      nodeConnections.get(edge.source).push(edge.target);
    });

    // Find the initial segment node (usually the first node)
    const initialNode = nodes.find(node => node.id === 'initial-segment') || nodes[0];
    if (initialNode) {
      sortedNodes.push(initialNode);
      visited.add(initialNode.id);
    }

    // Traverse the flow using BFS to maintain logical order
    const queue = [initialNode?.id];
    while (queue.length > 0) {
      const currentNodeId = queue.shift();
      const connectedNodes = nodeConnections.get(currentNodeId) || [];
      
      connectedNodes.forEach(targetNodeId => {
        if (!visited.has(targetNodeId)) {
          const targetNode = nodes.find(node => node.id === targetNodeId);
          if (targetNode) {
            sortedNodes.push(targetNode);
            visited.add(targetNodeId);
            queue.push(targetNodeId);
          }
        }
      });
    }

    // Add any remaining nodes that weren't connected
    nodes.forEach(node => {
      if (!visited.has(node.id)) {
        sortedNodes.push(node);
      }
    });

    return sortedNodes;
  };

  // Add this new function to handle journey save
  const handleSaveJourney = async () => {
    // Sort nodes in logical flow order before saving
    const sortedNodes = sortNodesInFlowOrder(nodes, edges);
    // Sort edges in serial, layer-wise order before saving
    const sortEdgesSerial = (edgesInput, nodesInput) => {
      const nodeById = new Map(nodesInput.map((n) => [n.id, n]));
      // Build adjacency from current edges
      const adjacency = new Map();
      edgesInput.forEach((e) => {
        if (!adjacency.has(e.source)) adjacency.set(e.source, []);
        adjacency.get(e.source).push(e.target);
      });
      // BFS from initial-segment to compute node visit order
      const visitOrder = new Map();
      const queue = [];
      const startId = nodesInput.find((n) => n.id === 'initial-segment')?.id || nodesInput[0]?.id;
      if (startId) {
        visitOrder.set(startId, 0);
        queue.push(startId);
      }
      while (queue.length > 0) {
        const cur = queue.shift();
        const nexts = adjacency.get(cur) || [];
        nexts.forEach((nid) => {
          if (!visitOrder.has(nid)) {
            visitOrder.set(nid, visitOrder.get(cur) + 1);
            queue.push(nid);
          }
        });
      }
      // Build an ordered list of source nodes as they appear by BFS node order
      const bfsNodesInOrder = [...nodesInput]
        .filter((n) => visitOrder.has(n.id))
        .sort((a, b) => visitOrder.get(a.id) - visitOrder.get(b.id));
      const sourceOrderList = [];
      const seenSources = new Set();
      bfsNodesInOrder.forEach((n) => {
        const hasOutgoing = edgesInput.some((e) => e.source === n.id);
        if (hasOutgoing && !seenSources.has(n.id)) {
          seenSources.add(n.id);
          sourceOrderList.push(n.id);
        }
      });
      // Add any remaining sources not reached in BFS at the end
      edgesInput.forEach((e) => {
        if (!seenSources.has(e.source)) {
          seenSources.add(e.source);
          sourceOrderList.push(e.source);
        }
      });
      const sourceIndex = new Map(sourceOrderList.map((id, idx) => [id, idx]));
      // Original index for stable tie-breaking
      const originalIndex = new Map(edgesInput.map((e, i) => [e, i]));
      // Group edges by explicit source index order
      const getSourceOrder = (edge) => sourceIndex.get(edge.source) ?? Number.MAX_SAFE_INTEGER;
      const getTargetY = (edge) => nodeById.get(edge.target)?.position?.y ?? Number.MAX_SAFE_INTEGER;
      const getTargetX = (edge) => nodeById.get(edge.target)?.position?.x ?? Number.MAX_SAFE_INTEGER;
      const sorted = [...edgesInput].sort((a, b) => {
        const soA = getSourceOrder(a);
        const soB = getSourceOrder(b);
        if (soA !== soB) return soA - soB;
        // Within the same source, prioritize non-empty labels first
        const aHasLabel = (String(a.label || '').trim().length > 0) ? 1 : 0;
        const bHasLabel = (String(b.label || '').trim().length > 0) ? 1 : 0;
        if (aHasLabel !== bHasLabel) return bHasLabel - aHasLabel; // non-empty on top
        // Then by target Y then X, then original
        const yA = getTargetY(a);
        const yB = getTargetY(b);
        if (yA !== yB) return yA - yB;
        const xA = getTargetX(a);
        const xB = getTargetX(b);
        if (xA !== xB) return xA - xB;
        return originalIndex.get(a) - originalIndex.get(b);
      });
      return sorted;
    };

    const sortedEdges = sortEdgesSerial(edges, nodes);

    const journeyData = {
      nodes: sortedNodes.map(node => {
        const nodeData = { ...node.data };
        
        // For sendEmail nodes, ensure emailConfig always includes fromEmail and replyEmail
        if (node.type === 'sendEmail') {
          // Initialize emailConfig if it doesn't exist
          if (!nodeData.emailConfig) {
            nodeData.emailConfig = {};
          }
          // Always ensure fromEmail and replyEmail are present (even if empty)
          // Use explicit check to preserve empty strings
          const currentFromEmail = nodeData.emailConfig.fromEmail;
          const currentReplyEmail = nodeData.emailConfig.replyEmail;
          
          nodeData.emailConfig = {
            ...nodeData.emailConfig,
            fromEmail: (currentFromEmail !== undefined && currentFromEmail !== null) ? currentFromEmail : '',
            replyEmail: (currentReplyEmail !== undefined && currentReplyEmail !== null) ? currentReplyEmail : '',
          };
        }
        
        return {
          id: node.id,
          type: node.type,
          position: node.position,
          data: {
            ...nodeData,
            label: nodeData.label || node.type,
            emailConfig: nodeData.emailConfig, // Include email configurations
            templateId: nodeData.templateId,
            templateTitle: nodeData.templateTitle,
          },
        };
      }),
      edges: sortedEdges,
      metadata: {
        createdAt: new Date().toISOString(),
        version: "1.0"
      }
    };

    // Log the journey data
    setLoaderSaveJourney(true);
    await saveJourneyData(id, journeyData);
  };

  // Add this new function
  const handleUpdateJourney = () => {
    // Validate inputs
    if (!journeyName.trim()) {
      Toast("Please enter a journey name", "E");
      return;
    }

    if (!selectedSegment) {
      Toast("Please select a segment", "E");
      return;
    }

    // Call the API through Redux action
    createJourney();
  };

  const onSuccessSave = () => {
    setTimeout(() => {
      setLoaderSaveJourney(false);
    }, 1000);
  }
  
  const onSuccessUpdate = () => {
    setShowJourneyDialog(false);
  }

  // Handle redirect after successful journey update
  useEffect(() => {
    if (journeyUpdateSuccess) {
      Toast("Journey updated successfull", "S");
      setLoaderSaveJourney(false);
      try {
        navigate('/home/journey');
        setTimeout(() => {
          if (window.location.pathname !== '/home/journey') {
            navigate('/journey');
          }
        }, 500);
        
        // Additional fallback with window.location
        setTimeout(() => {
          if (window.location.pathname !== '/home/journey' && window.location.pathname !== '/journey') {
            window.location.href = '/home/journey';
          }
        }, 1000);
        
        // Additional fallback with window.location
        setTimeout(() => {
          if (window.location.pathname !== '/home/journey' && window.location.pathname !== '/journey') {
            window.location.href = '/home/journey';
          }
        }, 1000);
        
        // Additional fallback with window.location
        setTimeout(() => {
          if (window.location.pathname !== '/home/journey' && window.location.pathname !== '/journey') {
            window.location.href = '/home/journey';
          }
        }, 1000);
      } catch (error) {
        console.error('Navigation error:', error);
      }
    }
  }, [journeyUpdateSuccess, navigate]);

  // Handle journey update errors
  useEffect(() => {
    if (journeyUpdateError) {
      Toast("Failed to update journey: " + journeyUpdateError, "E");
      setLoaderSaveJourney(false);
    }
  }, [journeyUpdateError]);

  // Cleanup effect to reset loader on unmount
  useEffect(() => {
    return () => {
      setLoaderSaveJourney(false);
    };
  }, []);

  //const [selectedDateNode, setSelectedDateNode] = useState(null);

  // Add handler for date selection
  const handleDateSelection = (date) => {
    if (selectedDateNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedDateNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  selectedDate: date,
                },
              }
            : node
        )
      );
    }
    setIsDateDrawerOpen(false);
    setSelectedDateNode(null);
  };

  // Add new state for wait time drawer
  //const [isWaitTimeDrawerOpen, setIsWaitTimeDrawerOpen] = useState(false);
  //const [selectedWaitTimeNode, setSelectedWaitTimeNode] = useState(null);

  // Add handler for wait time selection
  const handleWaitTimeSelection = (waitTimeData) => {
    if (selectedWaitTimeNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedWaitTimeNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  waitTimeConfig: waitTimeData,
                  label: `Wait for ${waitTimeData.value} ${waitTimeData.unit}`, // Update node label
                },
              }
            : node
        )
      );
    }
    setIsWaitTimeDrawerOpen(false);
    setSelectedWaitTimeNode(null);
  };

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  // Handle node hover to expand and show output options
  const onNodeMouseEnter = useCallback((event, node) => {
    setHoveredNodeId(node.id);
  }, []);

  const onNodeMouseLeave = useCallback(() => {
    // Don't clear hoveredNodeId on mouse leave - keep expanded state for connections
    // It will be cleared when clicking elsewhere or hovering another node
  }, []);

  // Clear expanded state when clicking on empty pane
  const onPaneClick = useCallback(() => {
    setHoveredNodeId(null);
  }, []);

  // Enhance edges with hover state for vibrant green glow
  // Only highlight edges originating from the hovered node's output handles
  const edgesWithHoverState = useMemo(() => {
    if (!hoveredNodeId) return edges;
    
    return edges.map(edge => {
      // Only highlight edges that originate from the hovered node (not edges coming into it)
      const isFromHoveredNode = edge.source === hoveredNodeId;
      return {
        ...edge,
        className: isFromHoveredNode ? 'edge-hovered-expanded' : '',
        style: {
          ...edge.style,
          strokeWidth: isFromHoveredNode ? 2.5 : (edge.style?.strokeWidth || 2),
          stroke: isFromHoveredNode ? '#28a745' : (edge.style?.stroke || '#b1b1b7'),
        },
        markerEnd: {
          ...edge.markerEnd,
          width: isFromHoveredNode ? 18 : (edge.markerEnd?.width || 20),
          height: isFromHoveredNode ? 18 : (edge.markerEnd?.height || 20),
          color: isFromHoveredNode ? '#28a745' : (edge.markerEnd?.color || '#b1b1b7'),
        },
      };
    });
  }, [edges, hoveredNodeId]);

  // Enhance nodes with hover state
  const nodesWithHoverState = useMemo(() => {
    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isHovered: hoveredNodeId === node.id,
      },
    }));
  }, [nodes, hoveredNodeId]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  
  if (node.type === 'sendEmail') {
    setSelectedEmailNode({
      ...node,
      data: {
        ...node.data,
        emailConfig: node.data.emailConfig || {
          campaignName: '',
          campaignTags: '',
          throttling: 'channel',
          template: node.data.templateId || '',
          testEmail: null,
          templateTitle: node.data.templateTitle || '',
          templateMessage: node.data.templateMessage || ''
        }
      }
    });
    setIsEmailDrawerOpen(true);
  } else if (node.type === 'sendWhatsApp') {
    setSelectedWhatsappNode({
      ...node,
      data: {
        ...node.data,
        whatsappConfig: node.data.whatsappConfig || {
          campaignName: '',
          campaignTags: '',
          throttling: 'channel',
          template: '',
          testEmail: null
        }
      }
    });
    setIsWhatsappDrawerOpen(true);
  } else if (node.type === 'sendPush') {
    setSelectedPushNode(node);
    setIsPushDrawerOpen(true);
  } else if (node.type === 'waitForDate') {
    setSelectedDateNode(node);
    setIsDateDrawerOpen(true);
  } else if (node.type === 'waitForSomeTime') {
    setSelectedWaitTimeNode(node);
    setIsWaitTimeDrawerOpen(true);
  } else if (node.type === 'hasDoneEvent') {
    setSelectedHasDoneEventNode(node);
    setIsHasDoneEventDrawerOpen(true);
  } else if (node.type === 'waitForTimeSlots') {
    setSelectedWaitTimeSlotsNode(node);
    setIsWaitTimeSlotsDrawerOpen(true);
  } else if (node.type === 'segment' && node.id === 'initial-segment') {
    setShowJourneyDialog(false);
  } else if (node.type === 'userAttribute') {
    setSelectedUserAttributeNode(node);
    setIsUserAttributeDrawerOpen(true);
  }
  }, []);

  // Add this handler function with your other handlers
const handleUserAttributeSave = (attributeData) => {
  if (selectedUserAttributeNode) {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedUserAttributeNode.id
          ? {
              ...node,
              data: {
                ...node.data,
                attributeConfig: attributeData,
                label: attributeData.label, // Update node label
              },
            }
          : node
      )
    );
  }
  setIsUserAttributeDrawerOpen(false);
  setSelectedUserAttributeNode(null);
};

// Add handler for time slots selection
const handleWaitTimeSlotsSelection = (timeSlotsData) => {
  if (selectedWaitTimeSlotsNode) {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedWaitTimeSlotsNode.id
          ? {
              ...node,
              data: {
                ...node.data,
                timeSlotsConfig: timeSlotsData,
                label: `Wait for ${timeSlotsData.days?.join(', ')} ${timeSlotsData.timeRanges?.length} slots`, // Update node label
              },
            }
          : node
      )
    );
  }
  setIsWaitTimeSlotsDrawerOpen(false);
  setSelectedWaitTimeSlotsNode(null);
};

  props.pageTypeHandler("Journey");

  return (
    <>
     <style>
       {`
         .header-div div { margin-top: 0px !IMPORTANT; }
       `}
     </style>
     {/* Add hidden file input */}
     <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".json"
        className="d-none"
      />





    <Container fluid className="mt-0">

{/* Add the floating save button */}

    {/* Floating Save Button */}
    <div className="save-button-container" style={{marginTop: '10px'}}>
      <button
        onClick={handleSaveJourney}
        disabled={loaderSaveJourney}
        className="btn btn-save d-flex align-items-center justify-content-center"
      >
        {loaderSaveJourney ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
          </>
        ) : (
          <>
            <BsFloppy className="me-2" /> Save
          </>
        )}
      </button>
     
    </div>

    <div className="save-button-container" style={{right: '170px',marginTop: '10px'}}>
     
      <button
        onClick={() => {
          setLoaderCloseJourney(true);
          setTimeout(() => {
            navigate("/journey");
          }, 100);
        }}
        disabled={loaderCloseJourney}
        className="btn btn-save d-flex align-items-center justify-content-center"
      >
        {loaderCloseJourney ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
          </>
        ) : (
          <>
            <BsXCircle className="me-2" /> Close
          </>
        )}
      </button>
    </div>

      <Row>
          <Col md={isSidebarCollapsed ? 1 : 3} className={`left-sidebar ${isSidebarCollapsed ? "collapsed" : ""}`} >
            {/* Sidebar Header */}
            <div className="sidebar-header mb-2">
              {!isSidebarCollapsed && (
                <span className="sidebar-title" title="{journeydetailas.title ? journeydetailas.title : 'Journey Builder'}">
                  {journeydetailas.title ? journeydetailas.title : 'Journey Builder'}
                </span>
              )}
              <div
                className="sidebar-toggle"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                title={isSidebarCollapsed ? "Expand" : "Collapse"}
              >
                {isSidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </div>
            </div>

            {/* Sidebar Content */}
            {!isSidebarCollapsed &&
              nodeTypesData.map((section, index) => (
                <CollapsibleSection
                  key={index}
                  title={section.title}
                  items={section.items}
                  color={section.color}
                />
              ))}
          </Col>


        <Col md={isSidebarCollapsed ? 11 : 9} className="flow-canvas position-relative">
          {journeyLoading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'rgba(255,255,255,0.6)', zIndex: 9999 }}>
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
                     <ReactFlowProvider>
     <ReactFlow
             key={id} // Force re-render when journey ID changes
             nodes={nodesWithHoverState}
             edges={edgesWithHoverState}
             onNodeMouseEnter={onNodeMouseEnter}
             onNodeMouseLeave={onNodeMouseLeave}
             onPaneClick={onPaneClick}
             nodeTypes={nodeTypes}
             onNodesChange={onNodesChange}
             onEdgesChange={onEdgesChange}
             onConnect={onConnect}
             onDrop={onDrop}
             onDragOver={onDragOver}
             onNodeClick={onNodeClick}
            onInit={(instance) => { reactFlowInstanceRef.current = instance; }}
             defaultViewport={{ x: 0, y: 0, zoom: .7 }}
             minZoom={0.2}
             maxZoom={4}
             fitViewOptions={{ padding: 100 }}
             
             className={`bg-light ${hoveredNodeId ? 'node-hover-active' : ''}`}
           >
            <Controls showZoom={true} />
            <Background />
            <MiniMap zoomable pannable />
              </ReactFlow>
  </ReactFlowProvider>
        </Col>
      </Row>
    </Container>

    {/* Bootstrap Modal for Edit Journey */}
    <div
      className={`modal fade ${showJourneyDialog ? "show d-block" : ""}`}
      tabIndex="-1"
      role="dialog"
      aria-labelledby="editJourneyModal"
      aria-hidden={!showJourneyDialog}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title" id="editJourneyModal">
              Edit Journey
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setShowJourneyDialog(false)}
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Journey Name</label>
              <input
                type="text"
                value={journeyName}
                onChange={(e) => setJourneyName(e.target.value)}
                className="form-control"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Segment</label>
              <div className="d-flex align-items-center gap-2">
                <select
                  value={selectedSegment}
                  onChange={(e) => setSelectedSegment(e.target.value)}
                  className="form-select"
                >
                  <option value="" disabled>
                    Select Segment
                  </option>
                  {segments.map((segment) => (
                    <option key={segment} value={segment}>
                      {segment}
                    </option>
                  ))}
                </select>
                <span className="text-muted">or</span>
                <button 
                  type="button" 
                  className="btn btn-outline-info"
                  onClick={() => {
                    // Close current dialog and navigate to journey listing for segment creation
                    setShowJourneyDialog(false);
                    navigate('/journey');
                  }}
                >
                  Create New Segment
                </button>
              </div>
            </div>

            {/* Segment Statistics */}
            {selectedSegment?.length > 0 && (
              <div className="border rounded p-3 mt-3">
                <div className="row text-center">
                  <div className="col">
                    <small className="text-muted">Total Users</small>
                    <div className="fw-bold">
                      {formatNumber(segmentStats.totalUsers)}
                    </div>
                  </div>
                  <div className="col">
                    <small className="text-muted">Known Users</small>
                    <div className="fw-bold">
                      {formatNumber(segmentStats.knownUsers)}
                    </div>
                  </div>
                  <div className="col">
                    <small className="text-muted">Unknown Users</small>
                    <div className="fw-bold">
                      {formatNumber(segmentStats.unknownUsers)}
                    </div>
                  </div>
                  <div className="col">
                    <small className="text-muted d-flex align-items-center justify-content-center">
                      Reachable Users
                      <span
                        className="ms-1"
                        data-bs-toggle="tooltip"
                        title="Users who can be reached through at least one channel"
                      >
                        <i className="bi bi-info-circle"></i>
                      </span>
                    </small>
                    <div className="fw-bold">
                      {formatNumber(segmentStats.reachableUsers)}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-muted small">
                  Segment contains all users
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowJourneyDialog(false)}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-info text-white"
              onClick={handleUpdateJourney}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>

    <EmailDrawer
        isOpen={isEmailDrawerOpen}
        onClose={handleEmailDrawerClose}
        onSave={handleEmailDrawerSave}
        initialData={selectedEmailNode}
        authToken={authToken}
        templates={templates}
        loading={templatesLoading}
        is_direct_email={is_direct_email}
      />

    {/* Other Drawers */}
    <WhatsappDrawer
      isOpen={isWhatsappDrawerOpen}
      onClose={handleWhatsappDrawerClose}
      onSave={handleWhatsappDrawerSave}
      initialData={selectedWhatsappNode?.data}
      authToken={authToken}
        templates={whatsappTemplates}
        loading={whatsappTemplatesLoading}
        nodeId={selectedWhatsappNode?.id}
      />

    <PushDrawer
        isOpen={isPushDrawerOpen}
        onClose={handlePushDrawerClose}
        onSave={handlePushDrawerSave}
        initialData={selectedPushNode?.data}
        authToken={authToken}
        nodeId={selectedPushNode?.id}
      />
      {/* Debug logging */}
      
    <DateDrawer
      isOpen={isDateDrawerOpen}
      onClose={() => {
        setIsDateDrawerOpen(false);
        setSelectedDateNode(null);
      }}
      onSave={(dateTime) => {
        handleDateSelection(dateTime);
      }}
      initialDate={selectedDateNode?.data?.selectedDate}
    />

    <WaitTimeDrawer
      isOpen={isWaitTimeDrawerOpen}
      onClose={() => {
        setIsWaitTimeDrawerOpen(false);
        setSelectedWaitTimeNode(null);
      }}
      onSave={handleWaitTimeSelection}
      initialData={selectedWaitTimeNode?.data?.waitTimeConfig}
    />

    <HasDoneEventDrawer
      isOpen={isHasDoneEventDrawerOpen}
      onClose={() => {
        setIsHasDoneEventDrawerOpen(false);
        setSelectedHasDoneEventNode(null);
      }}
      onSave={handleHasDoneEventSave}
      initialData={selectedHasDoneEventNode?.data}
    />

    <WaitTimeSlotsDrawer
      isOpen={isWaitTimeSlotsDrawerOpen}
      onClose={() => {
        setIsWaitTimeSlotsDrawerOpen(false);
        setSelectedWaitTimeSlotsNode(null);
      }}
      onSave={handleWaitTimeSlotsSelection}
      initialData={selectedWaitTimeSlotsNode?.data?.timeSlotsConfig}
    />
    <UserAttributeDrawer
      isOpen={isUserAttributeDrawerOpen}
      onClose={() => {
        setIsUserAttributeDrawerOpen(false);
        setSelectedUserAttributeNode(null);
      }}
      onSave={handleUserAttributeSave}
      initialData={selectedUserAttributeNode?.data?.attributeConfig}
    />
    </>
  );
};
export default JourneyBuilder;